import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { createGameHub } from "../api/hub";
import agent from "../api/agent";
import UserDataModal, { UserDataValues } from "../../components/UserDataModal";
import { toast } from "react-toastify";
import { env } from "../utils/env";
import { Campaign } from "../models/campaign";

export default function RemoteForm() {
  const { id } = useParams<{ id: string }>();
  const hubRef = useRef<any>(null);

  const [locked, setLocked] = useState(false);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [formState, setFormState] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!id) return;
      try {
        const fetched = await agent.Campaigns.details(id);
        if (!mounted) return;
        setCampaign(fetched);
        setFormState(fetched.formState);

        if (fetched.collectUserData !== 4) {
          Swal.fire({
            title: "Uzak form aktif değil",
            text: "Bu kampanya uzaktan form toplama için ayarlı değil.",
            icon: "info",
          });
        }
      } catch {
        Swal.fire("Kampanya yüklenemedi", "", "error");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id]);

  useEffect(() => {
    if (!id || !campaign) return;

    if (campaign.collectUserData !== 4) {
      if (hubRef.current) {
        hubRef.current.stop();
        hubRef.current = null;
      }
      return;
    }

    const hub = createGameHub(env!);
    hubRef.current = hub;

    hub.on("SessionState", (state: any) => {
      const isLocked = !!state.isLocked;
      setLocked(isLocked);
      if (isLocked) {
        Swal.fire({
          title: "Oyun sürüyor",
          text: "Lütfen oyunun bitmesini bekleyiniz.",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => Swal.showLoading(),
        });
      } else {
        Swal.close();
      }
    });
  
    hub.on("GameLocked", () => {
      setLocked(true);
      Swal.fire({
        title: "Oyun sürüyor",
        text: "Lütfen oyunun bitmesini bekleyiniz.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });
    });
  
    hub.on("GameUnlocked", () => {
      setLocked(false);
      Swal.close();
    });
  
    hub.on("FormAccepted", () => {
      // Lock UI, tell user the game is starting
      Swal.fire({
        title: "Form alındı",
        text: "Oyun başlıyor, lütfen ekrana bakınız.",
        icon: "success",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
      });
    });
  
    hub.on("FormRejected", (p: any) => {
      Swal.fire("Gönderilemedi", p?.reason === "GameInProgress" ? "Oyun sürüyor." : "Tekrar deneyin.", "error");
    });
  
    hub
      .start()
      .then(() => hub.invoke("JoinMobile", id))
      .catch(() => Swal.fire("Bağlantı kurulamadı", "", "error"));
  
    return () => {
      hub.stop();
      hubRef.current = null;
    };
  }, [id, campaign]);

  const hasDuplicatePlayer = async (cid: string, values: UserDataValues) => {
    if (!formState?.duplicateCheck) return false;
    const res = await agent.Games.exists(cid, {
      firstName: values.firstName,
      lastName: values.lastName,
      email: formState.formEmail ? values.email : undefined,
      phone: formState.formPhone ? values.phone : undefined,
    });
    return res.duplicate;
  };

  const onSubmit = async (values: UserDataValues) => {
    if (!id) return false;
    if (await hasDuplicatePlayer(id, values)) {
      toast.error("Aynı oyuncu bir kampanyada sadece bir kez oynayabilir.");
      return false; // keep modal open, do NOT reset anything
    }

    // Normalize payload to match SpinWheel buildPlayerData
    const payload = {
      id: undefined, // kiosk will assign
      campaignId: id,
      firstName: values.firstName || "",
      lastName: values.lastName || "",
      gender: formState.formGender ? (values.gender === 1 ? 1 : 2) : 0,
      email: formState.formEmail ? values.email || "" : "",
      phone: formState.formPhone ? values.phone || "" : "",
      company: formState.formCompany ? values.company || "" : "",
      formAddPermissionChecked: formState.formAddPermission ? values.formAddPermissionChecked || false : false,
      formAddPermission2Checked: formState.formAddPermission2 ? values.formAddPermission2Checked || false : false,
      bonusField: formState.bonusField ? values[formState.bonusField] || "" : "",
      bonusFieldLabel: formState.bonusField || "",
    };

    try {
      await hubRef.current.invoke("SubmitForm", id, payload);      
      return false; // Success handled by FormAccepted swal
    } catch {
      Swal.fire("Hata", "Form gönderilemedi.", "error");
      return false;
    }
  };

  let background = "";
  let backgroundSize = "";
  const screenColor = campaign?.screenStyle?.screenColor || "#E60805";

  if (campaign?.screenStyle?.screenBg) {
    if (campaign?.screenStyle?.screenCenterBg) {
      const screenBgUrl = campaign.screenStyle.screenBg.startsWith("http")
        ? campaign.screenStyle.screenBg
        : env + campaign.screenStyle.screenBg;
      const screenCenterBgUrl = campaign.screenStyle.screenCenterBg.startsWith("http")
        ? campaign.screenStyle.screenCenterBg
        : env + campaign.screenStyle.screenCenterBg;

      background = `url(${screenCenterBgUrl}) center ${
        campaign.screenStyle.screenCenterBgFromTop || "1300"
      }px no-repeat, url(${screenBgUrl}) center center no-repeat, ${screenColor}`;
      backgroundSize = "auto, cover, auto";
    } else {
      const screenBgUrl = campaign.screenStyle.screenBg.startsWith("http")
        ? campaign.screenStyle.screenBg
        : env + campaign.screenStyle.screenBg;
      background = `url(${screenBgUrl}) center center no-repeat, ${screenColor}`;
      backgroundSize = "cover, auto";
    }
  } else {
    backgroundSize = "auto";
    if (campaign?.screenStyle?.screenCenterBg) {
      const screenCenterBgUrl = campaign.screenStyle.screenCenterBg.startsWith("http")
        ? campaign.screenStyle.screenCenterBg
        : env + campaign.screenStyle.screenCenterBg;
      background = `url(${screenCenterBgUrl}) center ${
        campaign.screenStyle.screenCenterBgFromTop || "1300"
      }px no-repeat, ${screenColor}`;
    } else {
      background = screenColor;
    }
  }

  const styleBlock = campaign ? (
    <style>
      {campaign.screenStyle?.titleFromTop != 0 &&
        `
        .remote-screen h3 {
          margin-top: ${campaign.screenStyle.titleFromTop}px;
          max-width: ${campaign.screenStyle.titleWidth}%;
        }
      `}
      {campaign.screenStyle?.titleFontSize != 0 &&
        `
        .remote-screen h3 {
          font-size: ${campaign.screenStyle.titleFontSize}px;
        }
      `}
      {campaign.screenStyle?.logoHeight != 0 &&
        `
        .remote-screen .screen-logo {
          max-height: ${campaign.screenStyle.logoHeight}px;
        }
      `}
      {campaign.screenStyle?.logoFromTop != 0 &&
        `
        .remote-screen .screen-logo {
          margin-top: ${campaign.screenStyle.logoFromTop}px;
        }
      `}
      {campaign.customCSS || ""}
    </style>
  ) : null;

  if (loading) return null;

  if (campaign && campaign.collectUserData !== 4) {
    return (
      <>
        {styleBlock}
        <main
          className="remote-screen"
          style={{
            color: campaign.screenStyle?.textColor || "#fff",
            background,
            backgroundSize,
            minHeight: "100vh",
            display: "grid",
            placeItems: "center",
            textAlign: "center",
            padding: 24,
          }}
        >
          <div>
            <h2>Uzak form bu kampanya için aktif değil</h2>
            <p style={{ opacity: 0.8 }}>
              Lütfen etkinlik ekranından oynayın veya uzak form özelliği için kampanya ayarlarını güncelleyin.
            </p>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      {styleBlock}
      <main
        className="remote-screen"
        style={{
          color: campaign?.screenStyle?.textColor || "#fff",
          background,
          backgroundSize,
          minHeight: "100vh",
          display: "grid",
          gridTemplateRows: "auto 1fr",
        }}
      >
        {/* Branding same as kiosk */}
        {campaign?.screenStyle?.logo && (
          <img
            className="screen-logo"
            src={
              campaign.screenStyle.logo.startsWith("http")
                ? campaign.screenStyle.logo
                : env + campaign.screenStyle.logo
            }
            alt={campaign?.title || ""}
            style={{ justifySelf: "center" }}
          />
        )}
        {campaign?.screenStyle?.pageTitle && (
          <h3 style={{ justifySelf: "center" }}>{campaign.screenStyle.pageTitle}</h3>
        )}

        {/* Centered form */}
        <div style={{ display: "grid", placeItems: "center" }}>
          <UserDataModal
            open={!locked}
            // prevent manual close (modal closes only after submit+accept or when locked)
            onClose={() => {}}
            formState={formState}
            onSubmit={async (values) => onSubmit(values)}
            submitLabel="Gönder"
          />
        </div>
      </main>
    </>
  );
}